import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Wallet as WalletIcon, RefreshCw, Search, Lock, Zap,
  ChevronRight, Loader2, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';


function fmt(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-IN');
}


export function AdminWalletsPage() {
  const { admin, loading } = useAdminAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState(params.get('search') || '');
  const [tier, setTier] = useState(params.get('tier') || '');
  const [lockedOnly, setLockedOnly] = useState(false);
  const [fetching, setFetching] = useState(false);

  const refresh = useCallback(async () => {
    setFetching(true);
    try {
      const qs = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (search.trim()) qs.set('search', search.trim());
      if (tier) qs.set('tier', tier);
      if (lockedOnly) qs.set('is_locked', 'true');
      const r = await adminApi.get(`/admin/wallets?${qs.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load wallets');
    } finally { setFetching(false); }
  }, [page, pageSize, search, tier, lockedOnly]);

  useEffect(() => { if (admin) refresh(); }, [admin, refresh]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (search.trim()) next.set('search', search.trim());
    if (tier) next.set('tier', tier);
    setParams(next, { replace: true });
  }, [search, tier, setParams]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;
  if (admin.role !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 p-6">
        <div className="max-w-md text-center">
          <Lock className="w-8 h-8 mx-auto mb-3 text-amber-400" />
          <div className="text-lg font-semibold">Wallets are SuperAdmin-only</div>
          <div className="text-sm text-slate-400 mt-1">Ask a SuperAdmin to grant you access.</div>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="admin-wallets-page" className="min-h-screen" style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}>
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin?tab=wallets" data-testid="admin-wallets-back" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <WalletIcon className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">Credit Wallets</div>
            <div className="text-xs text-slate-400">Live balances, ledger, and manual adjustments per organization</div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[280px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="admin-wallets-search"
              placeholder="Search org name/id, owner email, GSTIN…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); refresh(); } }}
              className="bg-slate-800/50 border-slate-700 text-white text-sm pl-9 h-9"
            />
          </div>
          <select
            data-testid="admin-wallets-tier-filter"
            value={tier}
            onChange={(e) => { setTier(e.target.value); setPage(1); }}
            className="bg-slate-800/50 border border-slate-700 text-white text-xs rounded-md px-2 py-1.5"
          >
            <option value="">All tiers</option>
            <option value="pro">Pro only</option>
            <option value="standard">Standard only</option>
          </select>
          <label className="text-xs text-slate-300 flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={lockedOnly}
              onChange={(e) => { setLockedOnly(e.target.checked); setPage(1); }}
              data-testid="admin-wallets-locked-filter"
            />
            Locked only
          </label>
          <Button onClick={refresh} disabled={fetching} variant="outline" size="sm" data-testid="admin-wallets-refresh" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 ml-auto">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <span className="text-xs text-slate-500">{total.toLocaleString('en-IN')} orgs</span>
        </div>

        <div className="bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden">
          {fetching ? (
            <div className="p-10 flex items-center justify-center gap-2 text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">No matching organizations.</div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-wallets-table">
              <thead className="bg-slate-950/60">
                <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-2 text-left">Organization</th>
                  <th className="px-4 py-2 text-left w-32">Owner</th>
                  <th className="px-4 py-2 text-left w-20">Tier</th>
                  <th className="px-4 py-2 text-right w-24">Balance</th>
                  <th className="px-4 py-2 text-right w-24">Lifetime +</th>
                  <th className="px-4 py-2 text-right w-24">Lifetime −</th>
                  <th className="px-4 py-2 text-left w-20">State</th>
                  <th className="px-4 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {items.map((row) => (
                  <tr key={row.organization_id} className="text-slate-200 hover:bg-slate-800/30" data-testid={`admin-wallet-row-${row.organization_id}`}>
                    <td className="px-4 py-2.5">
                      <div className="text-white font-medium truncate max-w-[220px]">{row.organization_name || '—'}</div>
                      <div className="text-[10px] text-slate-500 font-mono truncate max-w-[220px]">{row.organization_id}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="text-slate-300 text-xs truncate max-w-[160px]">{row.owner_email || '—'}</div>
                      {row.owner_gstin && <div className="text-[10px] text-slate-500 font-mono">{row.owner_gstin}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${
                        row.license_tier === 'pro' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' : 'bg-slate-700/50 text-slate-300 border-slate-600'
                      }`}>{row.license_tier || 'none'}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-emerald-300 text-sm">{fmt(row.wallet?.balance)}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-400 text-xs">+{fmt(row.wallet?.lifetime_topup)}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-400 text-xs">−{fmt(row.wallet?.lifetime_usage)}</td>
                    <td className="px-4 py-2.5">
                      {row.wallet?.is_locked ? (
                        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border bg-rose-500/15 text-rose-300 border-rose-500/30 inline-flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Locked
                        </span>
                      ) : row.wallet?.exists ? (
                        <span className="text-[10px] uppercase tracking-wider text-emerald-400">Active</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">No wallet</span>
                      )}
                    </td>
                    <td className="px-2 py-2.5">
                      <Link to={`/admin/wallets/${row.organization_id}`} data-testid={`admin-wallet-open-${row.organization_id}`}>
                        <ChevronRight className="w-4 h-4 text-slate-500 hover:text-emerald-300" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {total > 0 && (
          <div className="flex items-center justify-between text-xs text-slate-400">
            <div>Page {page} of {totalPages}</div>
            <div className="flex gap-2">
              <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Prev</Button>
              <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Next</Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
